import React, { useState } from 'react'
import {
  Box,
  Chip,
  CircularProgress,
  Tooltip,
  Typography,
  Divider,
  Stack,
  Grid
} from '@mui/material'
import {
  Receipt,
  Inventory,
  People,
  Store,
  PointOfSale,
  Payment,
  AccountBalance,
  Storage as DataIcon,
  Sync as SyncIcon
} from '@mui/icons-material'
import { LightspeedIcon, XeroIcon, SkimmerIcon } from '../../SystemIcons'
import { apiClient, dataApi } from '../../../services/api'
import { formatNumber } from '../../../utils/formatting'
import { formatToLongDate } from '../../../utils/textUtils'
import { FranchisePartner } from './hooks/useFranchisePartnersData'
import { SyncSystem } from './hooks/useSyncOperations'

const STALE_THRESHOLD_DAYS = 3

const formatTimeAgo = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '--'
  const normalizedDate = dateStr.endsWith('Z') || dateStr.includes('+') || dateStr.includes('-', 10)
    ? dateStr
    : `${dateStr}Z`
  const date = new Date(normalizedDate)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMinutes = Math.floor(diffMs / (1000 * 60))

  if (diffMinutes < 1) return '1m'
  if (diffMinutes < 60) return `${diffMinutes}m`
  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) return `${diffHours}h`
  const diffDays = Math.floor(diffHours / 24)
  return `${diffDays}d`
}

const isStale = (dateStr: string | null | undefined): boolean => {
  if (!dateStr) return true
  const normalizedDate = dateStr.endsWith('Z') || dateStr.includes('+') || dateStr.includes('-', 10)
    ? dateStr
    : `${dateStr}Z`
  const date = new Date(normalizedDate)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = diffMs / (1000 * 60 * 60 * 24)
  return diffDays >= STALE_THRESHOLD_DAYS
}

const formatCompactCount = (count: number | null | undefined): string => {
  if (!count || count === 0) return '0'
  if (count < 1000) return count.toString()
  if (count < 10000) return `${(count / 1000).toFixed(1)}k`
  if (count < 100000) return `${Math.round(count / 1000)}k`
  return `${Math.round(count / 1000)}k`
}

interface DataTypeGroup {
  key: string
  label: string
  icon: string
  primary_type: string
  child_types: { key: string; label: string }[]
  display_format: string
}

interface SystemDataTypes {
  lightspeed: DataTypeGroup[]
  xero: DataTypeGroup[]
  skimmer: DataTypeGroup[]
}

interface DataCountBreakdown {
  [key: string]: number
}

interface SystemDataChipsProps {
  fp: FranchisePartner
  activeSyncs: { [key: string]: { sync_system: string } }
  syncingFps: { [key: string]: boolean }
  onTriggerSync: (fp: FranchisePartner, system: SyncSystem) => void
}

const SystemDataChips: React.FC<SystemDataChipsProps> = ({
  fp,
  activeSyncs,
  syncingFps,
  onTriggerSync
}) => {
  const [tooltipData, setTooltipData] = useState<{
    breakdown: DataCountBreakdown
    lastUpdated: string
  } | null>(null)
  const [tooltipLoading, setTooltipLoading] = useState(false)
  const [dataTypes, setDataTypes] = useState<SystemDataTypes | null>(null)
  const [loadedSystem, setLoadedSystem] = useState<string | null>(null)

  const isSystemSyncing = (system: string): boolean => {
    if (syncingFps[fp.fp_code]) return true
    // activeSyncs is now keyed by fp_code then sync_system
    const activeSync = activeSyncs[fp.fp_code]?.[system]
    if (activeSync) return true
    return false
  }

  const fetchTooltipData = async (system: string) => {
    if (loadedSystem === system && tooltipData) return

    setTooltipLoading(true)
    try {
      const [countsResponse, typesResponse] = await Promise.all([
        apiClient.get(`/admin/franchise-partners/${fp.fp_code}/data-counts`),
        dataApi.getDataTypes()
      ])
      setTooltipData({
        breakdown: countsResponse.data.breakdown,
        lastUpdated: countsResponse.data.last_updated
      })
      setDataTypes(typesResponse.data)
      setLoadedSystem(system)
    } catch (err) {
      console.error('Failed to fetch tooltip data:', err)
    } finally {
      setTooltipLoading(false)
    }
  }

  const getIconComponent = (iconName: string) => {
    const iconProps = { sx: { fontSize: 16 } }
    switch (iconName) {
      case 'Receipt': return <Receipt {...iconProps} />
      case 'Inventory': return <Inventory {...iconProps} />
      case 'People': return <People {...iconProps} />
      case 'Store': return <Store {...iconProps} />
      case 'PointOfSale': return <PointOfSale {...iconProps} />
      case 'Payment': return <Payment {...iconProps} />
      case 'AccountBalance': return <AccountBalance {...iconProps} />
      default: return <DataIcon {...iconProps} />
    }
  }

  const renderSystemTooltip = (
    system: 'lightspeed' | 'xero' | 'skimmer',
    onClickSync: () => void
  ) => {
    const systemNames: Record<string, string> = {
      lightspeed: 'Lightspeed',
      xero: 'Xero',
      skimmer: 'Skimmer'
    }

    const lastSyncAt = system === 'lightspeed' ? fp.lightspeed_last_sync_at
      : system === 'xero' ? fp.xero_last_sync_at
        : fp.skimmer_last_sync_at

    if (tooltipLoading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2">Loading...</Typography>
        </Box>
      )
    }

    if (!tooltipData || !dataTypes) {
      return (
        <Box sx={{ p: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Hover to load details
          </Typography>
        </Box>
      )
    }

    const groups = dataTypes[system]
    if (!groups) return null

    const hasData = groups.some(group =>
      (tooltipData.breakdown[group.primary_type] || 0) > 0 ||
      group.child_types.some(child => (tooltipData.breakdown[child.key] || 0) > 0)
    )

    return (
      <Box sx={{ p: 2, minWidth: 280, maxWidth: 350 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          {system === 'lightspeed' && <LightspeedIcon size={18} />}
          {system === 'xero' && <XeroIcon size={18} />}
          {system === 'skimmer' && <SkimmerIcon size={18} />}
          <Typography variant="subtitle2" fontWeight="bold">
            {systemNames[system]} - {fp.fp_code.toUpperCase()}
          </Typography>
        </Stack>

        <Divider sx={{ mb: 1.5 }} />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Last sync: {lastSyncAt ? `${formatTimeAgo(lastSyncAt)} ago (${formatToLongDate(lastSyncAt)})` : 'Never'}
        </Typography>

        {hasData ? (
          <>
            <Divider sx={{ mb: 1.5 }} />
            <Grid container spacing={0.5}>
              {groups.map((group) => {
                const primaryCount = tooltipData.breakdown[group.primary_type] || 0
                const childCounts = group.child_types.map(child => ({
                  ...child,
                  count: tooltipData.breakdown[child.key] || 0
                }))

                if (primaryCount === 0 && childCounts.every(child => child.count === 0)) {
                  return null
                }

                return (
                  <Grid item xs={12} key={group.key}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {getIconComponent(group.icon)}
                      <Box sx={{ flex: 1 }}>
                        {group.display_format === 'grouped' && childCounts.some(child => child.count > 0) ? (
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            <strong>{group.label}:</strong>{' '}
                            <span style={{ color: '#007f98', fontWeight: 'bold' }}>
                              {formatNumber(primaryCount)}
                            </span>
                            {childCounts.filter(child => child.count > 0).length > 0 && (
                              <span style={{ color: 'rgba(0,0,0,0.6)' }}>
                                {' '}({childCounts
                                  .filter(child => child.count > 0)
                                  .map(child => `${formatNumber(child.count)} ${child.label.toLowerCase()}`)
                                  .join(', ')})
                              </span>
                            )}
                          </Typography>
                        ) : (
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            <strong>{group.label}:</strong>{' '}
                            <span style={{ color: '#007f98', fontWeight: 'bold' }}>
                              {formatNumber(primaryCount)}
                            </span>
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </Grid>
                )
              })}
            </Grid>

            <Divider sx={{ my: 1.5 }} />

            <Typography variant="body2" fontWeight="bold">
              Total:{' '}
              <span style={{ color: '#007f98' }}>
                {formatNumber(
                  system === 'lightspeed' ? (fp.lightspeed_record_count || 0)
                    : system === 'xero' ? (fp.xero_record_count || 0)
                      : (fp.skimmer_record_count || 0)
                )} records
              </span>
            </Typography>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No data synced yet
          </Typography>
        )}

        <Divider sx={{ my: 1.5 }} />

        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          onClick={(e) => { e.stopPropagation(); onClickSync() }}
          sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
        >
          <SyncIcon sx={{ fontSize: 14, color: '#007f98' }} />
          <Typography variant="caption" color="primary" sx={{ fontWeight: 500 }}>
            Click to sync
          </Typography>
        </Stack>
      </Box>
    )
  }

  const renderChip = (
    system: 'lightspeed' | 'xero' | 'skimmer',
    icon: React.ReactNode,
    lastSyncAt: string | null | undefined,
    recordCount: number | null | undefined
  ) => {
    const syncing = isSystemSyncing(system)
    const stale = isStale(lastSyncAt)
    const timeAgo = formatTimeAgo(lastSyncAt)
    const count = formatCompactCount(recordCount)
    const neverSynced = !lastSyncAt

    const chipIcon = icon  // Always show the system icon

    const label = syncing ? (
      <CircularProgress size={14} sx={{ color: '#007f98' }} />
    ) : (
      `${timeAgo} · ${count}`
    )

    return (
      <Tooltip
        key={system}
        title={renderSystemTooltip(system, () => onTriggerSync(fp, system))}
        placement="bottom"
        arrow
        onOpen={() => fetchTooltipData(system)}
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: 'background.paper',
              color: 'text.primary',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: 3,
              maxWidth: 'none'
            }
          },
          arrow: {
            sx: {
              color: 'background.paper',
              '&::before': {
                border: '1px solid',
                borderColor: 'divider'
              }
            }
          }
        }}
      >
        <Chip
          icon={<Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>{chipIcon}</Box>}
          label={label}
          size="small"
          onClick={() => onTriggerSync(fp, system)}
          sx={{
            backgroundColor: syncing
              ? '#e3f2fd'
              : neverSynced
                ? '#f5f5f5'
                : stale
                  ? '#fff3e0'
                  : 'white',
            borderColor: syncing
              ? '#007f98'
              : neverSynced
                ? '#e0e0e0'
                : stale
                  ? '#ff9800'
                  : '#e0e0e0',
            color: syncing
              ? '#007f98'
              : neverSynced
                ? '#999'
                : stale
                  ? '#e65100'
                  : '#333',
            border: '1px solid',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '0.75rem',
            height: 28,
            '& .MuiChip-icon': {
              marginLeft: '4px'
            },
            '&:hover': {
              backgroundColor: syncing
                ? '#bbdefb'
                : neverSynced
                  ? '#eeeeee'
                  : stale
                    ? '#ffe0b2'
                    : '#f5f5f5',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }
          }}
        />
      </Tooltip>
    )
  }

  const chips = []

  if (fp.has_complete_lightspeed_connection) {
    chips.push(renderChip(
      'lightspeed',
      <LightspeedIcon size={16} />,
      fp.lightspeed_last_sync_at,
      fp.lightspeed_record_count
    ))
  }

  if (fp.has_complete_xero_connection) {
    chips.push(renderChip(
      'xero',
      <XeroIcon size={16} />,
      fp.xero_last_sync_at,
      fp.xero_record_count
    ))
  }

  if (chips.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
        No connections
      </Typography>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
      {chips}
    </Box>
  )
}

export default SystemDataChips
